<template>
  <div>
    <page-header
      icon="mdi-warehouse"
      :title="$t('routes.warehouse')"
      :subtitle="$t('warehouse_management_system')"
    >
      <template #actions v-if="!device.isMobile">
        <v-row no-gutters>
          <print-button @click="setPrinter" class="mr-2" />
          <excel-button @click="setExcel()" />
        </v-row>
      </template>
    </page-header>
    <!-- Warehouse Dialog -->
    <warehouse-dialog
      v-model="warehouseDialog"
      :edited-item="editedWarehouse"
      @create="onWarehouseCreated"
    />
    <empty-state
      v-if="!loading && !warehouses.length"
      icon="mdi-warehouse"
      :title="$t('no_warehouses')"
      :subtitle="$t('no_warehouses_added_yet')"
      class="ma-4"
    >
      <primary-button @click="warehouseDialog = true">
        <v-icon left>mdi-plus</v-icon>
        {{ $t("new_warehouse") }}
      </primary-button>
    </empty-state>
    <template v-else>
      <!-- Warehouse Filters Component -->
      <warehouse-filters
        :active-tab="activeTab"
        :warehouses="warehouses"
        :selected-warehouses.sync="selectedWarehouses"
        :selected-suppliers.sync="selectedSuppliers"
        :suppliers="$store.state.warehouse.suppliers"
        :filters="filters"
        :code-filter-groceries="codeFilterGroceries"
        :shifts="shifts"
        @toggle-warehouses="toggleWarehouses"
        @toggle-suppliers="toggleSuppliers"
        @update:filter="onFilterUpdate"
        @refresh="refreshData"
        @reset="resetFilters"
        @apply="refreshData"
      />

      <div
        class="mt-2"
        :class="{
          'px-6': device.isDesktop,
        }"
        v-if="companiesLoaded"
      >
        <!-- Header with tabs -->
        <v-tabs v-model="tab" background-color="primary" dark slider-color="black">
          <v-tab v-for="item in tabs" :key="item.title">
            <v-icon left>{{ item.icon }}</v-icon>
            {{ item.title }}
          </v-tab>
        </v-tabs>
        <v-tabs-items
          v-model="tab"
          v-if="filters.warehouse.length"
          touchless
          class="py-4 px-1"
        >
          <v-tab-item v-for="(item, i) in tabs" :key="item.title">
            <!-- 'Trebovanje' -->
            <component
              :is="item.component"
              v-if="tab === i"
              @changeTab="changeTab"
              :selected-grocery="selectedGrocery"
            ></component>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn fab small color="green darken-2" dark @click="setExcel()">
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="blue-grey" dark @click="setPrinter">
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <!-- Refresh Button -->
      <v-btn fab small color="blue" dark @click="refreshData">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import {
  WarehouseTabWarehouse,
  WarehouseTabInventory,
  WarehouseTabInvoice,
  WarehouseTabTransaction,
  WarehouseTabRequirement,
  WarehouseTabPurchase,
  WarehouseTabWriteOff,
  WarehouseTabReturn,
  WarehouseTabList,
  WarehouseTabEntry,
  WarehouseFilters,
  WarehouseTabOffersInvoices
} from "@/components/warehouse";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import PageHeader from "@/components/global/PageHeader.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import WarehouseDialog from "@/components/warehouse/dialogs/WarehouseDialog.vue";
import companyService from "@/services/company";

export default {
  mixins: [hasPrinting, hasExcel, hasSnackbar],
  components: {
    PageHeader,
    EmptyState,
    WarehouseDialog,
    WarehouseFilters,
  },
  data() {
    return {
      tab: 0,
      fabMenu: false,
      loading: false,
      warehouseDialog: false,
      editedWarehouse: {
        id: "",
        name: "",
        location: "",
        notes: "",
      },
      selectedGrocery: null,
      warehouses: [],
      selectedWarehouses: [],
      selectedSuppliers: [],
      shifts: [1, 2, 3, 4],
      companiesLoaded: false,
    };
  },
  created() {
    this.loadCompanies();
    this.fetchWarehouses();
  },
  computed: {
    tabs() {
      return [
        {
          title: this.$t("warehouse_requirement"),
          component: WarehouseTabRequirement,
          icon: "mdi-clipboard-list-outline",
          filters: {
            warehouse: true,
            show_type: true,
            shifts: true,
            date_from: true,
            date_to: true,
            suppliers: false,
            search: true,
          },
        },
        {
          title: this.$t("warehouse_inventory"),
          component: WarehouseTabInventory,
          icon: "mdi-archive-outline",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_from_readonly: true,
            date_to: false,
            suppliers: false,
            search: true,
          },
        },
        {
          title: this.$t("stocktaking"),
          component: WarehouseTabList,
          icon: "mdi-clipboard-check-outline",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_to: true,
            suppliers: false,
            search: true,
          },
        },
        {
          title: this.$t("warehouse_ordered"),
          component: WarehouseTabPurchase,
          icon: "mdi-cart-arrow-down",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_to: true,
            state_filter: true,
            suppliers: true,
            search: true,
            show_order: true,
            show_suppliers: true,
          },
        },
        {
          title: this.$t("warehouse.warehouse_replenishment"),
          component: WarehouseTabEntry,
          icon: "mdi-package-variant-plus",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_to: true,
            suppliers: true,
            search: true,
            code_filter: true,
          },
        },
        {
          title: this.$t("warehouse_issue"),
          component: WarehouseTabInvoice,
          icon: "mdi-receipt-text-arrow-right-outline",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_to: true,
            suppliers: false,
            search: true,
          },
        },
        {
          title: this.$t("warehouse_return"),
          component: WarehouseTabReturn,
          icon: "mdi-backup-restore",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_to: true,
            suppliers: false,
            search: true,
          },
        },
        {
          title: this.$t("write_off"),
          component: WarehouseTabWriteOff,
          icon: "mdi-delete-sweep-outline",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_to: true,
            suppliers: false,
            search: true,
          },
        },
        {
          title: this.$t("transactions"),
          component: WarehouseTabTransaction,
          icon: "mdi-history",
          filters: {
            warehouse: true,
            show_type: false,
            date_from: true,
            date_to: true,
            search: true,
            suppliers: true,
            code_filter: true,
            type_filter: true,
          },
        },
        {
          title: this.$t("warehouses"),
          component: WarehouseTabWarehouse,
          icon: "mdi-warehouse",
          filters: {
            warehouse: false,
            show_type: false,
            date_from: false,
            suppliers: false,
            date_to: false,
            search: true,
          },
        },
        {
          title: this.$t("offers_invoices"),
          component: WarehouseTabOffersInvoices,
          icon: "mdi-warehouse",
          filters: {
            warehouse: false,
            show_type: false,
            date_from: false,
            suppliers: false,
            date_to: false,
            search: true,
          },
        },
      ];
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    activeTab() {
      return this.tabs[this.tab];
    },
    codeFilterGroceries() {
      return this.$store.state.warehouse.codeFilterGroceries;
    },
  },
  methods: {
    async loadCompanies() {
      // Prevent multiple calls
      if (this.companiesLoaded) return;

      try {
        const res = await companyService.getCompanies();
        const companies = res.data || [];

        // Count companies by type
        const groupCount = companies.filter((c) => c.type === "group").length;
        const nonGroupCount = companies.length - groupCount;

        // Set default showType based on majority type
        if (groupCount > nonGroupCount) {
          this.$store.commit("warehouse/SET_FILTER", {
            key: "showType",
            value: "daily-group",
          });
        } else {
          this.$store.commit("warehouse/SET_FILTER", {
            key: "showType",
            value: "daily",
          });
        }
      } catch (error) {
        console.error("Error loading companies:", error);
        // Set default on error
        this.$store.commit("warehouse/SET_FILTER", {
          key: "showType",
          value: "daily",
        });
      } finally {
        this.companiesLoaded = true;
      }
    },
    refreshData() {
      // Trigger refresh for the active tab component
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    changeTab(item) {
      this.tab = item.tab;
      this.selectedGrocery = item.grocery;
    },
    toggleWarehouses() {
      if (this.selectedWarehouses.length === this.warehouses.length) {
        this.selectedWarehouses = [];
      } else {
        this.selectedWarehouses = [...this.warehouses];
      }
    },
    async fetchWarehouses() {
      this.loading = true;
      try {
        await this.$store.dispatch("warehouse/fetchItems");
        this.warehouses = [...this.$store.state.warehouse.items];
        this.selectedWarehouses = [...this.warehouses];
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_warehouses"));
      } finally {
        this.loading = false;
      }
    },
    onWarehouseCreated(warehouse) {
      // Add new warehouse to local list and select it
      this.warehouses.push(warehouse);
      this.selectedWarehouses = [...this.warehouses];
      // Reset edited warehouse
      this.editedWarehouse = {
        id: "",
        name: "",
        location: "",
        notes: "",
      };
    },
    toggleSuppliers() {
      if (
        this.selectedSuppliers.length === this.$store.state.warehouse.suppliers.length
      ) {
        this.selectedSuppliers = [];
      } else {
        this.selectedSuppliers = [...this.$store.state.warehouse.suppliers];
      }
    },
    resetFilters() {
      this.selectedWarehouses = [...this.warehouses];
      this.selectedSuppliers = [];
      this.filters.search = "";
      this.filters.status = null;
      this.filters.documentType = null;
      this.filters.groceryCode = null;
      this.filters.selectedShifts = [1, 2, 3, 4];
      const today = new Date().toISOString().substr(0, 10);
      this.$store.commit("warehouse/SET_DATE", [today, today]);
    },
    onFilterUpdate({ key, value }) {
      this.filters[key] = value;
    },
  },
  watch: {
    tab(newTab) {
      // When switching to inventory tab (index 1), set date to today
      if (newTab === 1) {
        const today = new Date().toISOString().substr(0, 10);
        this.$store.commit("warehouse/SET_DATE", [today, today]);
      }
    },
    selectedSuppliers(val) {
      this.filters.suppliers = [...val];
    },
    selectedWarehouses(val) {
      this.filters.warehouse = [...val];
    },
    "filters.warehouse"(val) {
      if (Array.isArray(val) && val.length !== this.selectedWarehouses.length) {
        this.selectedWarehouses = this.warehouses.filter((w) =>
          val.some((v) => v && v.id === w.id)
        );
      }
    },
    "filters.suppliers"(val) {
      if (Array.isArray(val) && val.length !== this.selectedSuppliers.length) {
        this.selectedSuppliers = this.$store.state.warehouse.suppliers.filter((s) =>
          val.some((v) => v && v.id === s.id)
        );
      }
    },
  },
};
</script>
